import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from '@/hooks/lib/invalidate-loan-activity';
import { api } from '@/lib/api';

export interface LoanCondition {
  id: string;
  loanId: string;
  description: string;
  category:
    | 'DOCUMENTATION'
    | 'COMPLIANCE'
    | 'SECURITY'
    | 'INSURANCE'
    | 'VALUATION'
    | 'LEGAL'
    | 'FINANCIAL'
    | 'OTHER';
  type: 'PRECEDENT' | 'SUBSEQUENT';
  status: 'OUTSTANDING' | 'SATISFIED' | 'WAIVED' | 'OVERDUE' | 'NOT_APPLICABLE';
  dueDate?: string; // ISO date
  satisfiedDate?: string; // ISO date
  waivedDate?: string; // ISO date
  waiverReason?: string;
  documentReference?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConditionTemplate {
  id: string;
  description: string;
  category: LoanCondition['category'];
  type: 'PRECEDENT' | 'SUBSEQUENT';
  commonDueDate?: number; // days from loan disbursement
}

function unwrapListResponse<T>(
  payload: T[] | { data?: T[] | null; items?: T[] | null; results?: T[] | null } | null | undefined,
): T[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  return [];
}

// Custom endpoint with type filter — keep manual
export function useConditions(loanId: string, type?: 'PRECEDENT' | 'SUBSEQUENT') {
  return useQuery<LoanCondition[]>({
    queryKey: ['conditions', loanId, type],
    queryFn: async () => {
      const params = type ? `?type=${type}` : '';
      const { data } = await api.get(`/loans/${loanId}/conditions${params}`);
      return unwrapListResponse<LoanCondition>(
        data as
          | LoanCondition[]
          | { data?: LoanCondition[]; items?: LoanCondition[]; results?: LoanCondition[] },
      );
    },
    enabled: !!loanId,
  });
}

// Custom endpoint — keep manual
export function useCreateCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      loanId,
      ...payload
    }: Omit<LoanCondition, 'id' | 'createdAt' | 'updatedAt'> & { loanId: string }) => {
      const { data } = await api.post<LoanCondition>(`/loans/${loanId}/conditions`, payload);
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['conditions', vars.loanId] });
      invalidateLoanActivity(qc, vars.loanId);
    },
  });
}

// Custom action endpoint /satisfy — keep manual
export function useSatisfyCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      loanId,
      conditionId,
      documentUrl,
    }: {
      loanId: string;
      conditionId: string;
      documentUrl?: string;
    }) => {
      const { data } = await api.patch<LoanCondition>(
        `/loans/${loanId}/conditions/${conditionId}/satisfy`,
        { documentUrl },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['conditions', vars.loanId] });
      invalidateLoanActivity(qc, vars.loanId);
    },
  });
}

// Custom action endpoint /waive — keep manual
export function useWaiveCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      loanId,
      conditionId,
      reason,
    }: {
      loanId: string;
      conditionId: string;
      reason: string;
    }) => {
      const { data } = await api.patch<LoanCondition>(
        `/loans/${loanId}/conditions/${conditionId}/waive`,
        { reason },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['conditions', vars.loanId] });
      invalidateLoanActivity(qc, vars.loanId);
    },
  });
}

// Global read-only endpoint — keep manual
export function useConditionTemplates() {
  return useQuery<ConditionTemplate[]>({
    queryKey: ['condition-templates'],
    queryFn: async () => {
      const { data } = await api.get('/conditions/templates');
      return unwrapListResponse<ConditionTemplate>(
        data as
          | ConditionTemplate[]
          | {
              data?: ConditionTemplate[];
              items?: ConditionTemplate[];
              results?: ConditionTemplate[];
            }
          | null
          | undefined,
      );
    },
  });
}

// Global read-only endpoint — keep manual
export function useOverdueConditions() {
  return useQuery<LoanCondition[]>({
    queryKey: ['conditions-overdue'],
    queryFn: async () => {
      const { data } = await api.get('/conditions/overdue');
      return unwrapListResponse<LoanCondition>(
        data as
          | LoanCondition[]
          | { data?: LoanCondition[]; items?: LoanCondition[]; results?: LoanCondition[] }
          | null
          | undefined,
      );
    },
  });
}
